import { useQuery, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { deserializeEvaluation } from '@teetsh/app/src/containers/CarnetsDeNotes/queries/serializer';
import { cdnKeys } from '@teetsh/app/src/containers/CarnetsDeNotes/queries/keys';
import { format } from 'utils/time/format';
import { isoDatePattern } from 'utils/time/datePatterns';
import { useQueryErrorNotification } from 'containers/App/Notifications/useQueryErrorNotification';
import { useTranslation } from 'react-i18next';

interface Filters {
  startDate?: Date;
  endDate?: Date;
  cycle?: 'cycle2' | 'cycle3';
}

function getEvaluations({
  schoolyearId,
  schoolId,
  cdnId,
  filters,
}: {
  schoolyearId: string;
  schoolId: string;
  cdnId: string;
  filters?: Filters;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/carnets-de-notes/${cdnId}/evaluations`;
  if (filters) {
    const { startDate, endDate, cycle } = filters;
    const params = new URLSearchParams();
    if (startDate) {
      params.append('startDate', format(startDate, 'fr-FR', isoDatePattern));
    }
    if (endDate) {
      params.append('endDate', format(endDate, 'fr-FR', isoDatePattern));
    }
    if (cycle) {
      params.append('cycle', cycle);
    }
    return request(`${requestURL}?${params.toString()}`, {
      method: 'GET',
    }).then(({ data }: { data: any[] }) =>
      data.map(ev => deserializeEvaluation(ev)),
    );
  }
  return request(requestURL, {
    method: 'GET',
  }).then(({ data }: { data: any[] }) =>
    data.map(ev => deserializeEvaluation(ev)),
  );
}

export function useEvaluations(cdnId: string, filters?: Filters) {
  const queryClient = useQueryClient();
  const { schoolyear, school } = useSchoolSchoolyear();
  const { t } = useTranslation(['carnet']);
  const query = useQuery({
    queryKey: cdnKeys.cdnEvaluations(
      schoolyear.id,
      school.id,
      cdnId,
      JSON.stringify(filters),
    ),
    queryFn: () =>
      getEvaluations({
        schoolyearId: schoolyear.id,
        schoolId: school.id,
        cdnId: cdnId || '',
        filters,
      }).then(evaluations => {
        (evaluations || []).forEach(evaluation => {
          queryClient.setQueryData(
            cdnKeys.cdnEvaluationDetails(
              schoolyear.id,
              school.id,
              cdnId,
              evaluation.id,
            ),
            evaluation,
          );
        });
        return evaluations;
      }),
    placeholderData: [
      {
        id: '',
        carnetDeNotesId: cdnId,
        date: new Date(),
        notes: [],
        order: 0,
        referential: {
          cycle: 'Cycle 2',
          matieres: [],
          objectifs: [],
          domaines: [],
          competences: [],
        },
        name: 'Eval',
      },
    ],
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: !!cdnId,
  });

  useQueryErrorNotification({
    isError: query.isError,
    message: t('carnet:messages.evaluation.getError_other'),
  });
  return query;
}
